import React, { useState, useEffect, useMemo, useCallback } from "react";
import { logFieldDefinitions } from "../../utils/fieldDefinitionsDebug";
import {
  Grid,
  Select,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Chip,
  IconButton,
  Autocomplete,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useCompanies } from "../../hooks/useCompanies";
import { useChannels } from "../../hooks/useChannels";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import { useRequests } from "../../hooks/useRequests";
import { useTypes } from "../../hooks/useTypes";
import { useClientTags } from "../../hooks/useClientTags";
import StatusSelector from "./StatusSelector";
import RequiredTasksDialog from "./RequiredTasksDialog";
import { RequestFieldInput } from "../CustomFieldsBlock";
import {
  useFieldDefinitions,
  searchRequestFieldOptions,
  resolveVisibleRequestFields,
  parseFieldDefinitionsPayload,
} from "../../hooks/useFieldDefinitions";
import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { getRequestStatusChangeErrorMessage } from "../../utils/status";
import {
  getAutomationBlockMessage,
  isAutomationRuleBlockError,
} from "../../utils/automationBlockNotice";
import AutomationActionBlockedDialog from "../AutomationActionBlockedDialog";
import ProjectChangeConfirmDialog from "./ProjectChangeConfirmDialog";
import WhatsappAttributionPanel from "./WhatsappAttributionPanel";
import TypeClassificationSuggestionBar from "./TypeClassificationSuggestionBar";
import { isTypeActive, mergePinnedTypesFromConversation, formatTypeOptionLabel } from "../../utils/typeSubtypeCascade";
import { useSocketContext } from "../../context/SocketContext";

/** GET /types?tree=1 retorna só raízes com `children`; cascata de subtipos precisa da lista plana. */
function flattenTypesTree(nodes, parentId = null) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = n.id;
    const idPai =
      n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : parentId;
    const nome = n.nome ?? n.name ?? String(id);
    const { children: _c, ...rest } = n;
    const item = { ...rest, id: Number(id), id_pai: idPai, nome };
    return [item, ...flattenTypesTree(n.children || [], Number(id))];
  });
}

const sameTypeId = (a, b) =>
  a != null &&
  a !== "" &&
  b != null &&
  b !== "" &&
  String(a) === String(b);

const REQUEST_ID_SNAKE_KEYS = {
  projectId: "id_projeto",
  channelId: "id_origem",
  typeId: "id_tipo",
  subtype1: "id_subtipo1",
  subtype2: "id_subtipo2",
  subtype3: "id_subtipo3",
  userId: "id_usuario",
  agentId: "id_agente",
  potential: "potencial",
};

/** Lê campo da solicitação em camelCase ou snake_case (API, lista ou legado). */
function pickRequestField(conv, key) {
  if (!conv) return null;
  const camel = conv[key];
  if (camel != null && camel !== "") return camel;
  const snake = REQUEST_ID_SNAKE_KEYS[key];
  if (!snake) return null;
  const alt = conv[snake];
  if (alt != null && alt !== "") return alt;
  return null;
}

/** Valor de Select MUI: string estável para coincidir com MenuItem (evita tela em branco). */
function toSelectId(value) {
  if (value === null || value === undefined || value === "") return "";
  if (value === 0 || value === "0") return "";
  return String(value);
}

/** Valor para input datetime-local a partir do retorno da API (YYYY-MM-DD HH:mm:ss). */
function nextContactToInputValue(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  return s.replace(" ", "T").slice(0, 16);
}

const RequestDetails = ({
  selectedConversation,
  onSelectedConversationUpdate,
  onRefetchConversationDetails,
}) => {
  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const tagsToString = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "");

  const buildRequestDetailsFromConversation = (conv) => ({
    status: toSelectId(conv?.status),
    projectId: toSelectId(pickRequestField(conv, "projectId")),
    channelId: toSelectId(pickRequestField(conv, "channelId")),
    typeId: toSelectId(pickRequestField(conv, "typeId")),
    typeType: toSelectId(conv?.typeType),
    subtype1: toSelectId(pickRequestField(conv, "subtype1")),
    subtype2: toSelectId(pickRequestField(conv, "subtype2")),
    subtype3: toSelectId(pickRequestField(conv, "subtype3")),
    userId: toSelectId(pickRequestField(conv, "userId")),
    agentId: toSelectId(pickRequestField(conv, "agentId")),
    potential: toSelectId(pickRequestField(conv, "potential")),
    nextContactAt: nextContactToInputValue(
      conv?.nextContactAt ?? conv?.dataproximocontato
    ),
    nf: conv?.nf ?? "",
    tags: parseTags(conv?.tags ?? ""),
  });

  const [requestDetails, setRequestDetails] = useState(() =>
    buildRequestDetailsFromConversation(selectedConversation)
  );
  const [typeSuggestion, setTypeSuggestion] = useState(
    () => selectedConversation?.typeSuggestion ?? null
  );
  const { socket } = useSocketContext();
  const [requestTagInput, setRequestTagInput] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const { data: tagOptions } = useClientTags({ search: tagSearch, limit: 200, includeUsed: false });
  const allowedTagNames = (tagOptions || [])
    .filter((t) => (t?.status == null ? true : Number(t.status) === 1))
    .map((t) => String(t?.nome || "").trim())
    .filter(Boolean);

  /* Re-sincroniza detalhes ao trocar a conversa ou quando o payload completo chega */
  useEffect(() => {
    if (!selectedConversation?.id) return;
    setRequestDetails(buildRequestDetailsFromConversation(selectedConversation));
  }, [selectedConversation?.id, selectedConversation]);

  /* Sugestão: só reseta no id ou quando o parent traz typeSuggestion explícito.
     Evita apagar sugestão do socket quando selectedConversation muda de referência. */
  useEffect(() => {
    if (!selectedConversation?.id) {
      setTypeSuggestion(null);
      return;
    }
    setTypeSuggestion(selectedConversation?.typeSuggestion ?? null);
  }, [selectedConversation?.id, selectedConversation?.typeSuggestion]);

  useEffect(() => {
    if (!socket || !selectedConversation?.id) return undefined;
    const leadId = String(selectedConversation.id);
    const onSuggestion = (payload) => {
      if (!payload || String(payload.conversationId) !== leadId) return;
      if (payload.suggestion) setTypeSuggestion(payload.suggestion);
    };
    const onUpdated = (payload) => {
      if (!payload || String(payload.conversationId) !== leadId) return;
      setTypeSuggestion(null);
      const chain = payload.chain;
      if (chain) {
        setRequestDetails((prev) => ({
          ...prev,
          typeId: chain.id_tipo != null ? String(chain.id_tipo) : prev.typeId,
          subtype1: chain.id_subtipo1 != null ? String(chain.id_subtipo1) : "",
          subtype2: chain.id_subtipo2 != null ? String(chain.id_subtipo2) : "",
          subtype3: chain.id_subtipo3 != null ? String(chain.id_subtipo3) : "",
        }));
        onSelectedConversationUpdate?.({
          typeId: chain.id_tipo,
          subtype1: chain.id_subtipo1,
          subtype2: chain.id_subtipo2,
          subtype3: chain.id_subtipo3,
          typeClassificationSource: payload.fonte || "ai_auto",
          typeSuggestion: null,
        });
      }
      onRefetchConversationDetails?.();
    };
    socket.on("lead_type_suggestion", onSuggestion);
    socket.on("lead_type_updated", onUpdated);
    return () => {
      socket.off("lead_type_suggestion", onSuggestion);
      socket.off("lead_type_updated", onUpdated);
    };
  }, [
    socket,
    selectedConversation?.id,
    onSelectedConversationUpdate,
    onRefetchConversationDetails,
  ]);

  const [loadingFields, setLoadingFields] = useState({});
  const { data: projectsData } = useCompanies();
  const { data: channelsData } = useChannels();
  const { data: typesData } = useTypes();
  const typesFlat = useMemo(
    () => flattenTypesTree(typesData?.types ?? []),
    [typesData?.types]
  );
  const typesFlatForSelect = useMemo(
    () => mergePinnedTypesFromConversation(typesFlat, selectedConversation),
    [typesFlat, selectedConversation]
  );
  // Permite atribuir/transferir também para ChatBots (ura=1).
  const { data: usersData } = useUsers(1, 1000, "", "todos", { order: "nome" });
  const { data: agentsData } = useAgents(1, 200, "");
  const { updateMutation } = useRequests();
  const agentsList = agentsData?.agents ?? [];
  const usersList = usersData?.users ?? [];

  const { byKey: configByKey } = useWorkspaceConfig();
  const {
    data: fieldDefs,
    isLoading: fieldDefsLoading,
    isFetching: fieldDefsFetching,
    isError: fieldDefsError,
    error: fieldDefsErrorDetail,
    status: fieldDefsStatus,
  } = useFieldDefinitions({
    typeId: selectedConversation?.typeId ?? requestDetails.typeId,
    subtype1: selectedConversation?.subtype1 ?? requestDetails.subtype1,
    subtype2: selectedConversation?.subtype2 ?? requestDetails.subtype2,
    subtype3: selectedConversation?.subtype3 ?? requestDetails.subtype3,
    projectId: selectedConversation?.projectId ?? requestDetails.projectId,
    leadId: selectedConversation?.id,
    context: "chat",
    enabled: !!selectedConversation?.id,
  });

  const parsedFieldDefs = useMemo(
    () => parseFieldDefinitionsPayload(fieldDefs),
    [fieldDefs]
  );

  const visibleRequestFields = useMemo(
    () =>
      resolveVisibleRequestFields({
        apiRequest: parsedFieldDefs.request,
        configByKey,
      }),
    [parsedFieldDefs.request, configByKey]
  );

  const showFieldDefsSpinner =
    (fieldDefsLoading || fieldDefsFetching) && visibleRequestFields.length === 0;

  useEffect(() => {
    logFieldDefinitions("RequestDetails:state", {
      conversationId: selectedConversation?.id,
      typeId: requestDetails.typeId,
      subtype1: requestDetails.subtype1,
      fieldDefsStatus,
      fieldDefsLoading,
      fieldDefsFetching,
      fieldDefsError,
      fieldDefsErrorMessage: fieldDefsErrorDetail?.message,
      rawFieldDefsKeys: fieldDefs ? Object.keys(fieldDefs) : [],
      parsedRequestLen: parsedFieldDefs.request?.length ?? 0,
      parsedVisibleKeys: (parsedFieldDefs.request || [])
        .filter((d) => d.visible !== false)
        .map((d) => `${d.key}:${d.label}`),
      visibleRequestFieldsCount: visibleRequestFields.length,
      visibleRequestFieldKeys: visibleRequestFields.map((d) => d.key),
      showFieldDefsSpinner,
    });
  }, [
    selectedConversation?.id,
    requestDetails.typeId,
    requestDetails.subtype1,
    fieldDefs,
    fieldDefsStatus,
    fieldDefsLoading,
    fieldDefsFetching,
    fieldDefsError,
    fieldDefsErrorDetail,
    parsedFieldDefs.request,
    visibleRequestFields,
    showFieldDefsSpinner,
  ]);

  const searchLookupOptions = useCallback(
    async (field, q) => {
      const current =
        field === "produto"
          ? selectedConversation?.produto
          : selectedConversation?.campanha;
      const list = await searchRequestFieldOptions({
        field,
        q,
        typeId: requestDetails.typeId,
        projectId: requestDetails.projectId,
      });
      if (current && String(current).trim()) {
        const c = String(current).trim();
        if (!list.some((x) => String(x).trim() === c)) {
          return [c, ...list];
        }
      }
      return list;
    },
    [
      requestDetails.typeId,
      requestDetails.projectId,
      selectedConversation?.produto,
      selectedConversation?.campanha,
    ]
  );

  const requestCustomValues = useMemo(() => {
    const cf = selectedConversation?.customFields;
    const custom =
      cf && typeof cf === "object" && !Array.isArray(cf) ? cf : {};
    return {
      produto: selectedConversation?.produto ?? custom.produto ?? "",
      campanha: selectedConversation?.campanha ?? custom.campanha ?? "",
      data_adicional:
        selectedConversation?.data_adicional ?? custom.data_adicional ?? "",
      texto_adicional:
        selectedConversation?.texto_adicional ?? custom.texto_adicional ?? "",
      ...custom,
    };
  }, [selectedConversation]);

  const handleCustomFieldCommit = (key, value) => {
    if (!selectedConversation?.id) return;
    const def = (parsedFieldDefs.request || []).find((d) => d.key === key);
    const payload = { id: selectedConversation.id };
    const successPayload = {};

    if (!def || def.legacy) {
      payload[key] = value;
      successPayload[key] = value;
    } else {
      payload.customFields = { [key]: value };
      successPayload.customFields = {
        ...(selectedConversation?.customFields || {}),
        [key]: value,
      };
    }

    setLoadingFields((prev) => ({ ...prev, [`cf_${key}`]: true }));
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setLoadingFields((prev) => ({ ...prev, [`cf_${key}`]: false }));
        onSelectedConversationUpdate?.(successPayload);
        if (typeof onRefetchConversationDetails === "function") {
          onRefetchConversationDetails();
        }
      },
      onError: () => {
        setLoadingFields((prev) => ({ ...prev, [`cf_${key}`]: false }));
        setSnackbar({
          open: true,
          message: "Erro ao atualizar o campo.",
          severity: "error",
        });
      },
    });
  };

  const [subtypes, setSubtypes] = useState({
    subtype1: [],
    subtype2: [],
    subtype3: [],
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [requiredTasksDialog, setRequiredTasksDialog] = useState({ open: false, tasks: [] });
  const [automationBlockDialog, setAutomationBlockDialog] = useState({
    open: false,
    message: "",
  });
  const [projectChangeDialog, setProjectChangeDialog] = useState({
    open: false,
    nextProjectId: "",
    fromName: "",
    toName: "",
  });

  /* =================== Helpers de defaults de subtipos =================== */
  /** Pipeline: `default`=1 primeiro; senão menor `ordem`, depois `id`. */
  const sortTypesByPipeline = (arr) =>
    [...arr].sort((a, b) => {
      const da = Number(a.default) === 1 ? 0 : 1;
      const db = Number(b.default) === 1 ? 0 : 1;
      if (da !== db) return da - db;
      const oa = a.ordem != null && a.ordem !== "" ? Number(a.ordem) : Infinity;
      const ob = b.ordem != null && b.ordem !== "" ? Number(b.ordem) : Infinity;
      if (oa !== ob) return oa - ob;
      return Number(a.id) - Number(b.id);
    });

  const findDefaultChildId = (parentId) => {
    if (!parentId || !typesFlat?.length) return null;
    const all = typesFlat.filter((t) => sameTypeId(t.id_pai, parentId));
    if (!all.length) return null;
    const active = all.filter((t) => isTypeActive(t));
    const base = sortTypesByPipeline(active.length ? active : all);
    return base[0]?.id ?? null;
  };

  const resolveDefaultChainForType = (typeId) => {
    const s1 = findDefaultChildId(typeId);
    const s2 = s1 != null ? findDefaultChildId(s1) : null;
    const s3 = s2 != null ? findDefaultChildId(s2) : null;
    return { s1, s2, s3 };
  };

  /* =================== Cascata de subtipos =================== */
  // Opções: apenas ativos, mais o valor já vinculado (mesmo inativo/excluído).
  const filterSubtypeOptions = (children, selectedId) =>
    (children || []).filter(
      (t) => isTypeActive(t) || sameTypeId(t.id, selectedId)
    );

  // 1) quando muda o typeId -> recarrega lista de opções de Subtipo 1
  useEffect(() => {
    if (requestDetails.typeId) {
      const s1 = filterSubtypeOptions(
        typesFlatForSelect.filter((t) =>
          sameTypeId(t.id_pai, requestDetails.typeId)
        ),
        requestDetails.subtype1
      );
      setSubtypes((prev) => ({ ...prev, subtype1: s1 }));
    } else {
      setSubtypes((prev) => ({ ...prev, subtype1: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDetails.typeId, requestDetails.subtype1, typesFlatForSelect]);

  // 2) quando muda o Subtipo 1 -> recarrega lista de opções de Subtipo 2
  useEffect(() => {
    if (requestDetails.subtype1) {
      const s2 = filterSubtypeOptions(
        typesFlatForSelect.filter((t) =>
          sameTypeId(t.id_pai, requestDetails.subtype1)
        ),
        requestDetails.subtype2
      );
      setSubtypes((prev) => ({ ...prev, subtype2: s2 }));
    } else {
      setSubtypes((prev) => ({ ...prev, subtype2: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDetails.subtype1, requestDetails.subtype2, typesFlatForSelect]);

  // 3) quando muda o Subtipo 2 -> recarrega lista de opções de Subtipo 3
  useEffect(() => {
    if (requestDetails.subtype2) {
      const s3 = filterSubtypeOptions(
        typesFlatForSelect.filter((t) =>
          sameTypeId(t.id_pai, requestDetails.subtype2)
        ),
        requestDetails.subtype3
      );
      setSubtypes((prev) => ({ ...prev, subtype3: s3 }));
    } else {
      setSubtypes((prev) => ({ ...prev, subtype3: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDetails.subtype2, requestDetails.subtype3, typesFlatForSelect]);

  /* =================== Atualização no backend =================== */
  const apiFieldMap = {
    status: "status",
    projectId: "id_projeto",
    channelId: "id_origem",
    typeId: "id_tipo",
    subtype1: "id_subtipo1",
    subtype2: "id_subtipo2",
    subtype3: "id_subtipo3",
    userId: "id_usuario",
    agentId: "id_agente",
    potential: "potencial",
    nextContactAt: "dataproximocontato",
    nf: "nf",
    tags: "tags",
  };

  const handleRequestTagAdd = () => {
    const t = requestTagInput.trim();
    if (!t) return;
    if (allowedTagNames.length && !allowedTagNames.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setSnackbar({
        open: true,
        message: `Esta tag (${t}) não é permitida.`,
        severity: "warning",
      });
      setRequestTagInput("");
      return;
    }
    const next = [...requestDetails.tags];
    if (next.includes(t)) {
      setRequestTagInput("");
      return;
    }
    next.push(t);
    handleFieldChange("tags", tagsToString(next));
    setRequestTagInput("");
  };

  const handleRequestTagRemove = (index) => {
    const next = requestDetails.tags.filter((_, i) => i !== index);
    handleFieldChange("tags", tagsToString(next));
  };

  const SELECT_ID_FIELDS = new Set([
    "projectId",
    "channelId",
    "typeId",
    "subtype1",
    "subtype2",
    "subtype3",
    "userId",
    "agentId",
    "potential",
    "status",
  ]);

  const OPEN_LEAD_STATUSES = new Set([0, 1]);

  const resolveCompanyName = useCallback(
    (id) => {
      if (id == null || id === "") return "";
      const row = projectsData?.companies?.find((p) => String(p.id) === String(id));
      return row?.nome || row?.name || `Empresa #${id}`;
    },
    [projectsData?.companies]
  );

  const handleFieldChange = (field, value, options = {}) => {
    const nextValue = SELECT_ID_FIELDS.has(field) ? toSelectId(value) : value;
    if (requestDetails[field] === nextValue) return;

    // Empresa: com atendimento aberto, confirma antes — próxima msg pode abrir solicitação nova.
    if (
      field === "projectId" &&
      !options.skipProjectConfirm &&
      OPEN_LEAD_STATUSES.has(Number(requestDetails.status))
    ) {
      setProjectChangeDialog({
        open: true,
        nextProjectId: nextValue,
        fromName: resolveCompanyName(requestDetails.projectId),
        toName: resolveCompanyName(nextValue),
      });
      return;
    }

    const updates = { [field]: nextValue };
    const payload = { id: selectedConversation.id };
    let successPayload = { [field]: nextValue };

    if (field === "agentId") {
      payload.id_agente = nextValue || null;
      payload.id_usuario = nextValue ? null : requestDetails.userId;
      updates.userId = nextValue ? "" : requestDetails.userId;
      const agent = agentsList.find((a) => String(a.id) === String(nextValue));
      successPayload = {
        ...updates,
        id_agente: payload.id_agente,
        id_usuario: payload.id_usuario,
        agentName: agent ? (agent.name ?? agent.nome) : null,
      };
    } else if (field === "userId") {
      payload.id_usuario = nextValue || null;
      payload.id_agente = nextValue ? null : requestDetails.agentId;
      updates.agentId = nextValue ? "" : requestDetails.agentId;
      successPayload = {
        ...updates,
        id_usuario: payload.id_usuario,
        id_agente: payload.id_agente,
        agentName: null,
      };
    } else if (field === "typeId") {
      // Ao mudar o tipo, preenche automaticamente Subtipo 1/2/3 com os defaults (se existirem)
      const { s1, s2, s3 } = resolveDefaultChainForType(nextValue);

      updates.subtype1 = toSelectId(s1);
      updates.subtype2 = toSelectId(s2);
      updates.subtype3 = toSelectId(s3);

      payload.id_tipo = nextValue;
      payload.id_subtipo1 = s1 ?? null;
      payload.id_subtipo2 = s2 ?? null;
      payload.id_subtipo3 = s3 ?? null;

      successPayload = {
        ...successPayload,
        id_tipo: nextValue,
        id_subtipo1: s1 ?? null,
        id_subtipo2: s2 ?? null,
        id_subtipo3: s3 ?? null,
      };
    } else if (field === "subtype1") {
      // Ao mudar o Subtipo 1, define automaticamente Subtipo 2/3 defaults (se existirem)
      const s2 = findDefaultChildId(nextValue);
      const s3 = s2 != null ? findDefaultChildId(s2) : null;

      updates.subtype2 = toSelectId(s2);
      updates.subtype3 = toSelectId(s3);

      payload.id_subtipo1 = nextValue;
      payload.id_subtipo2 = s2 ?? null;
      payload.id_subtipo3 = s3 ?? null;

      successPayload = {
        ...successPayload,
        id_subtipo1: nextValue,
        id_subtipo2: s2 ?? null,
        id_subtipo3: s3 ?? null,
      };
    } else if (field === "subtype2") {
      // Ao mudar o Subtipo 2, define automaticamente Subtipo 3 default (se existir)
      const s3 = findDefaultChildId(nextValue);

      updates.subtype3 = toSelectId(s3);

      payload.id_subtipo2 = nextValue;
      payload.id_subtipo3 = s3 ?? null;

      successPayload = {
        ...successPayload,
        id_subtipo2: nextValue,
        id_subtipo3: s3 ?? null,
      };
    } else if (field === "tags") {
      // tags: value é string para API; estado local mantém array em requestDetails.tags
      const tagArray = typeof value === "string" ? parseTags(value) : (Array.isArray(value) ? value : []);
      updates.tags = tagArray;
      payload.tags = typeof value === "string" ? value : tagsToString(value);
      successPayload.tags = payload.tags;
    } else if (field === "status") {
      payload.status = nextValue;
      successPayload.status = nextValue;
      if (Number(nextValue) !== 3) {
        updates.nextContactAt = "";
        successPayload.nextContactAt = null;
        successPayload.dataproximocontato = null;
      }
    } else if (field === "nextContactAt") {
      const raw = value && String(value).trim() ? String(value).trim() : null;
      payload.dataproximocontato = raw;
      successPayload.dataproximocontato = raw;
      successPayload.nextContactAt = raw;
    } else {
      const apiField = apiFieldMap[field] ?? field;
      payload[apiField] = nextValue;
      successPayload[apiField] = nextValue;
    }

    setRequestDetails((prev) => ({ ...prev, ...updates }));
    const loadingKeys = field === "agentId" || field === "userId" ? { userId: true, agentId: true } : { [field]: true };
    setLoadingFields((prev) => ({ ...prev, ...loadingKeys }));

    updateMutation.mutate(
      payload,
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: "Atualização bem-sucedida!",
            severity: "success",
          });
          const doneKeys = field === "agentId" || field === "userId" ? { userId: false, agentId: false } : { [field]: false };
          setLoadingFields((prev) => ({ ...prev, ...doneKeys }));
          onSelectedConversationUpdate?.(successPayload);
          if (typeof onRefetchConversationDetails === "function") {
            onRefetchConversationDetails();
          }
        },
        onError: (err) => {
          if (err?.status === 409 && err?.code === "required_tasks_pending") {
            const pending = err?.data?.pendingTasks || [];
            setRequiredTasksDialog({ open: true, tasks: pending });
            if (typeof onRefetchConversationDetails === "function") {
              onRefetchConversationDetails();
            } else if (selectedConversation) {
              setRequestDetails(buildRequestDetailsFromConversation(selectedConversation));
            }
            const doneKeys =
              field === "agentId" || field === "userId"
                ? { userId: false, agentId: false }
                : { [field]: false };
            setLoadingFields((prev) => ({ ...prev, ...doneKeys }));
            return;
          }
          const unknown =
            err?.data?.unknown ||
            err?.unknown ||
            err?.data?.data?.unknown ||
            null;
          const statusFallback = "Não foi possível alterar o status.";
          const defaultFallback = "Erro ao atualizar o campo.";
          const errorMessage = Array.isArray(unknown) && unknown.length
            ? `Tags não permitidas: ${unknown.join(", ")}`
            : field === "status"
              ? getRequestStatusChangeErrorMessage(err, statusFallback)
              : getRequestStatusChangeErrorMessage(err, defaultFallback);

          if (isAutomationRuleBlockError(err)) {
            setAutomationBlockDialog({
              open: true,
              message: getAutomationBlockMessage(err, errorMessage),
            });
          } else {
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: Array.isArray(unknown) && unknown.length ? "warning" : "error",
            });
          }
          if (selectedConversation) {
            setRequestDetails(buildRequestDetailsFromConversation(selectedConversation));
          }
          const doneKeys = field === "agentId" || field === "userId" ? { userId: false, agentId: false } : { [field]: false };
          setLoadingFields((prev) => ({ ...prev, ...doneKeys }));
        },
      }
    );
  };

  const handleProjectChangeCancel = () => {
    setProjectChangeDialog({ open: false, nextProjectId: "", fromName: "", toName: "" });
  };

  const handleProjectChangeConfirm = () => {
    const nextId = projectChangeDialog.nextProjectId;
    setProjectChangeDialog({ open: false, nextProjectId: "", fromName: "", toName: "" });
    if (nextId === "" || nextId == null) return;
    handleFieldChange("projectId", nextId, { skipProjectConfirm: true });
  };

  const handleAssigneeChange = (next) => {
    console.log('handleAssigneeChange', next, selectedConversation);
    if (!selectedConversation?.id || !next) return;

    const payload = { id: selectedConversation.id };
    const updates = {};
    const successPayload = {};

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;

      updates.userId = null;
      updates.agentId = null;

      successPayload.id_usuario = null;
      successPayload.id_agente = null;
      successPayload.userId = null;
      successPayload.agentId = null;
      successPayload.assignedTo = null;
      successPayload.userName = null;
      successPayload.agentName = null;
    } else if (next.type === "agent") {
      let agentId = null;

      if (next.raw) {
        const a = next.raw;
        agentId =
          a.id ??
          a.id_agente ??
          a.agentId ??
          null;
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agentsList.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId =
            found.id ??
            found.id_agente ??
            found.agentId ??
            null;
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;

      updates.agentId = payload.id_agente;
      updates.userId = null;

      successPayload.id_agente = payload.id_agente;
      successPayload.id_usuario = null;
      successPayload.agentId = payload.id_agente;
      successPayload.userId = null;
      successPayload.agentName = next.label;
      successPayload.assignedTo = null;
      successPayload.userName = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;

      updates.userId = payload.id_usuario;
      updates.agentId = null;

      successPayload.id_usuario = payload.id_usuario;
      successPayload.id_agente = null;
      successPayload.userId = payload.id_usuario;
      successPayload.agentId = null;
      successPayload.assignedTo = next.label;
      successPayload.userName = next.label;
      successPayload.agentName = null;
    }

    setRequestDetails((prev) => ({
      ...prev,
      userId: toSelectId(updates.userId),
      agentId: toSelectId(updates.agentId),
    }));

    setLoadingFields((prev) => ({
      ...prev,
      userId: true,
      agentId: true,
    }));

    updateMutation.mutate(payload, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: "Responsável atualizado com sucesso!",
          severity: "success",
        });
        setLoadingFields((prev) => ({
          ...prev,
          userId: false,
          agentId: false,
        }));
        onSelectedConversationUpdate?.(successPayload);
        if (typeof onRefetchConversationDetails === "function") {
          onRefetchConversationDetails();
        }
      },
      onError: (err) => {
        if (isAutomationRuleBlockError(err)) {
          setAutomationBlockDialog({
            open: true,
            message: getAutomationBlockMessage(err, "Erro ao atualizar o responsável."),
          });
        } else {
          setSnackbar({
            open: true,
            message: getRequestStatusChangeErrorMessage(err, "Erro ao atualizar o responsável."),
            severity: "error",
          });
        }
        setLoadingFields((prev) => ({
          ...prev,
          userId: false,
          agentId: false,
        }));
      },
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const renderLoadingOrIcon = (field) =>
    loadingFields[field] ? <CircularProgress size={20} /> : null;

  // console.log("RequestDetails renderizado", requestDetails.typeType===0, requestDetails.typeType, requestDetails, selectedConversation);
  /* =================== Render =================== */
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StatusSelector
            currentStatus={requestDetails.status}
            onStatusChange={(newStatus) =>
              handleFieldChange("status", newStatus)
            }
            loading={!!loadingFields.status}
            disabled={!!loadingFields.status}
          />
        </Grid>

        {(selectedConversation?.whatsappAttribution || selectedConversation?.metaReferral) && (
          <Grid item xs={12}>
            <WhatsappAttributionPanel
              attribution={
                selectedConversation.whatsappAttribution || selectedConversation.metaReferral
              }
            />
          </Grid>
        )}

        {Number(requestDetails.status) === 3 && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Data e hora do próximo contato"
              type="datetime-local"
              value={requestDetails.nextContactAt || ""}
              onChange={(e) => handleFieldChange("nextContactAt", e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Você receberá um lembrete neste horário (solicitação atribuída a você)."
              disabled={!selectedConversation?.id}
              InputProps={{
                endAdornment: renderLoadingOrIcon("nextContactAt"),
              }}
            />
          </Grid>
        )}

        {/* Tags da solicitação */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
            {(requestDetails.tags || []).map((tag, index) => (
              <Chip
                key={`${tag}-${index}`}
                label={tag}
                size="small"
                onDelete={() => handleRequestTagRemove(index)}
                sx={{ mb: 0.5 }}
              />
            ))}
            <Autocomplete
              options={allowedTagNames}
              value={null}
              inputValue={requestTagInput}
              onInputChange={(_e, v) => {
                setRequestTagInput(v);
                setTagSearch(v);
              }}
              onChange={(_e, value) => {
                if (!value) return;
                setRequestTagInput("");
                const next = [...(requestDetails.tags || []), value];
                handleFieldChange("tags", tagsToString(next));
              }}
              sx={{ minWidth: 220, flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Selecione uma tag"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <IconButton size="small" onClick={handleRequestTagAdd} aria-label="Adicionar tag" sx={{ mr: 0.5 }}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Grid>

        {/* Empresa */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={requestDetails.projectId ?? ""}
              onChange={(e) =>
                handleFieldChange("projectId", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("projectId")}
            >
              {projectsData?.companies?.map((project) => (
                <MenuItem key={project.id} value={String(project.id)}>
                  {project.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {OPEN_LEAD_STATUSES.has(Number(requestDetails.status)) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              Com atendimento aberto, trocar a empresa pode fazer a próxima mensagem do WhatsApp
              abrir uma solicitação nova.
            </Typography>
          ) : null}
        </Grid>

        {/* Canal */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Canal</InputLabel>
            <Select
              value={requestDetails.channelId ?? ""}
              onChange={(e) =>
                handleFieldChange("channelId", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("channelId")}
            >
              {channelsData?.channels?.map((channel) => (
                <MenuItem key={channel.id} value={String(channel.id)}>
                  {channel.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Tipo */}
        <Grid item xs={12}>
          <TypeClassificationSuggestionBar
            conversationId={selectedConversation?.id}
            suggestion={typeSuggestion}
            onApplied={(sug) => {
              setTypeSuggestion(null);
              const chain = sug?.chain;
              if (chain) {
                setRequestDetails((prev) => ({
                  ...prev,
                  typeId: chain.id_tipo != null ? String(chain.id_tipo) : prev.typeId,
                  subtype1: chain.id_subtipo1 != null ? String(chain.id_subtipo1) : "",
                  subtype2: chain.id_subtipo2 != null ? String(chain.id_subtipo2) : "",
                  subtype3: chain.id_subtipo3 != null ? String(chain.id_subtipo3) : "",
                }));
                onSelectedConversationUpdate?.({
                  typeId: chain.id_tipo,
                  subtype1: chain.id_subtipo1,
                  subtype2: chain.id_subtipo2,
                  subtype3: chain.id_subtipo3,
                  typeClassificationSource: "ai_accepted",
                  typeSuggestion: null,
                });
              }
              onRefetchConversationDetails?.();
            }}
            onDismissed={() => setTypeSuggestion(null)}
          />
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={requestDetails.typeId ?? ""}
              onChange={(e) =>
                handleFieldChange("typeId", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("typeId")}
            >
              {typesFlatForSelect
                .filter(
                  (t) =>
                    !t.id_pai &&
                    (isTypeActive(t) ||
                      sameTypeId(t.id, requestDetails.typeId))
                )
                .map((type) => (
                  <MenuItem
                    key={type.id}
                    value={String(type.id)}
                    sx={
                      !isTypeActive(type)
                        ? { color: "text.secondary", fontStyle: "italic" }
                        : undefined
                    }
                  >
                    {formatTypeOptionLabel(type)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Subtipo 1 */}
        {subtypes.subtype1?.length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subtipo 1</InputLabel>
              <Select
                value={requestDetails.subtype1 ?? ""}
                onChange={(e) =>
                  handleFieldChange("subtype1", e.target.value)
                }
                endAdornment={renderLoadingOrIcon("subtype1")}
              >
                {subtypes.subtype1.map((st) => (
                  <MenuItem
                    key={st.id}
                    value={String(st.id)}
                    sx={
                      !isTypeActive(st)
                        ? { color: "text.secondary", fontStyle: "italic" }
                        : undefined
                    }
                  >
                    {formatTypeOptionLabel(st)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Subtipo 2 */}
        {subtypes.subtype2?.length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subtipo 2</InputLabel>
              <Select
                value={requestDetails.subtype2 ?? ""}
                onChange={(e) =>
                  handleFieldChange("subtype2", e.target.value)
                }
                endAdornment={renderLoadingOrIcon("subtype2")}
              >
                {subtypes.subtype2.map((st) => (
                  <MenuItem
                    key={st.id}
                    value={String(st.id)}
                    sx={
                      !isTypeActive(st)
                        ? { color: "text.secondary", fontStyle: "italic" }
                        : undefined
                    }
                  >
                    {formatTypeOptionLabel(st)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Subtipo 3 */}
        {subtypes.subtype3?.length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subtipo 3</InputLabel>
              <Select
                value={requestDetails.subtype3 ?? ""}
                onChange={(e) =>
                  handleFieldChange("subtype3", e.target.value)
                }
                endAdornment={renderLoadingOrIcon("subtype3")}
              >
                {subtypes.subtype3.map((st) => (
                  <MenuItem
                    key={st.id}
                    value={String(st.id)}
                    sx={
                      !isTypeActive(st)
                        ? { color: "text.secondary", fontStyle: "italic" }
                        : undefined
                    }
                  >
                    {formatTypeOptionLabel(st)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Responsável: fila / usuário / agente de IA */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Responsável</InputLabel>
            <Select
              value={(() => {
                if (requestDetails.agentId) {
                  return `agent:${requestDetails.agentId}`;
                }
                if (requestDetails.userId) {
                  return `user:${requestDetails.userId}`;
                }
                return "queue";
              })()}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "queue") {
                  handleAssigneeChange({ type: "queue" });
                  return;
                }

                if (typeof value === "string" && value.startsWith("agent:")) {
                  const id = value.split(":")[1];
                  const agent =
                    agentsList.find(
                      (a) =>
                        String(
                          a.id ?? a.id_agente ?? a.agentId ?? ""
                        ) === String(id)
                    ) || null;

                  const label =
                    agent?.name ??
                    agent?.nome ??
                    selectedConversation?.agentName ??
                    "Agente de IA";

                  handleAssigneeChange({
                    type: "agent",
                    id,
                    label,
                    raw: agent || undefined,
                  });
                  return;
                }

                if (typeof value === "string" && value.startsWith("user:")) {
                  const id = value.split(":")[1];
                  const user =
                    usersList.find(
                      (u) => String(u.id ?? "") === String(id)
                    ) || null;

                  const label =
                    user?.nome ??
                    user?.name ??
                    selectedConversation?.userName ??
                    selectedConversation?.assignedTo ??
                    "Usuário";

                  handleAssigneeChange({
                    type: "user",
                    id,
                    label,
                  });
                }
              }}
              endAdornment={
                loadingFields.userId || loadingFields.agentId
                  ? renderLoadingOrIcon("userId")
                  : null
              }
            >
              <MenuItem value="queue">Fila (sem atribuição)</MenuItem>

              {agentsList.length > 0 && (
                <MenuItem disabled sx={{ fontWeight: "bold", opacity: 0.8 }}>
                  Agentes de IA
                </MenuItem>
              )}

              {agentsList
                .map((a) => {
                  const id = a.id ?? a.id_agente ?? a.agentId;
                  const label =
                    a.name ?? a.nome ?? `Agente de IA #${id ?? "sem-id"}`;
                  return (
                    <MenuItem
                      key={`agent-${id ?? "sem-id"}`}
                      value={`agent:${id}`}
                      sx={{ fontWeight: "bold" }}
                    >
                      {label}
                    </MenuItem>
                  );
                })}

              {usersList.length > 0 && (
                <MenuItem disabled sx={{ fontWeight: "bold", opacity: 0.8 }}>
                  Usuários
                </MenuItem>
              )}

              {usersList.map((user) => (
                <MenuItem
                  key={`user-${user.id}`}
                  value={`user:${user.id}`}
                >
                  {user.nome ?? user.name ?? `Usuário #${user.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {showFieldDefsSpinner && (
          <Grid item xs={12}>
            <CircularProgress size={20} />
          </Grid>
        )}

        {visibleRequestFields.map((def) => (
          <Grid item xs={12} key={def.key}>
            <RequestFieldInput
              definition={def}
              value={requestCustomValues[def.key]}
              onCommit={(_key, val) => handleCustomFieldCommit(def.key, val)}
              onSearchOptions={
                def.type === "select" ? searchLookupOptions : undefined
              }
              loadingField={!!loadingFields[`cf_${def.key}`]}
              disabled={
                updateMutation.isLoading || !!loadingFields[`cf_${def.key}`]
              }
            />
          </Grid>
        ))}

        {/* Potencial */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="request-potential-label">Potencial</InputLabel>
            <Select
              labelId="request-potential-label"
              label="Potencial"
              value={requestDetails.potential ?? ""}
              onChange={(e) =>
                handleFieldChange("potential", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("potential")}
            >
              <MenuItem value="1">Baixo</MenuItem>
              <MenuItem value="2">Médio</MenuItem>
              <MenuItem value="3">Alto</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {requestDetails.typeType===0 && <Grid item xs={12}>
          <TextField
            label="NF"
            fullWidth
            value={requestDetails.nf}
            onChange={(e) => handleFieldChange("nf", e.target.value)}
          />
        </Grid>}

      </Grid>

      {/* Snackbar */}
      <AutomationActionBlockedDialog
        open={automationBlockDialog.open}
        message={automationBlockDialog.message}
        onClose={() => setAutomationBlockDialog({ open: false, message: "" })}
      />

      <ProjectChangeConfirmDialog
        open={projectChangeDialog.open}
        fromName={projectChangeDialog.fromName}
        toName={projectChangeDialog.toName}
        onCancel={handleProjectChangeCancel}
        onConfirm={handleProjectChangeConfirm}
        confirming={!!loadingFields.projectId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 560 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <RequiredTasksDialog
        open={requiredTasksDialog.open}
        onClose={() => setRequiredTasksDialog({ open: false, tasks: [] })}
        pendingTasks={requiredTasksDialog.tasks}
        leadId={selectedConversation?.id}
      />
    </>
  );
};

export default RequestDetails;
